<script lang="ts" setup>
import { PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/vue/24/outline";
import axios from "axios";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { refDebounced } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import KnocSearch from "./KnocSearch.vue";
import ReachExpander from "./ReachExpander.vue";
import GrantCredentialsDialog, { type ACLRequirements, type GrantCredentials } from "./GrantCredentialsDialog.vue";
import api from "../services/api";
import { useThemeStore } from "../pinia/theme";
import { usePersistentIPDeviceStore } from "../pinia/persistentIPDevices";
import { useAuthStore, type ACL } from "../pinia/auth";
import { agenticGrantStatusKind } from "../shared/services/agenticGrantStatus";
import { countryCodeToFlag, PLACEHOLDER_FLAG, NOT_FOUND_FLAG } from "../utils/countryCodeToFlag";

const DEFAULT_RETRY_AFTER_SECONDS = 30;
const IP_LOOKUP_DEBOUNCE_MS = 300;

const props = defineProps<{
  acls: ACL[];
}>();

const emit = defineEmits<{ summary: [{ count: number; hasPending: boolean }] }>();

const { t } = useI18n();
const themeStore = useThemeStore();
const theme = computed(() => themeStore.accent);
const highContrast = computed(() => themeStore.highAccessibility);
const authStore = useAuthStore();
const deviceStore = usePersistentIPDeviceStore();

// One card per device. Saved cards carry the server's device id; drafts have
// no id yet (they exist only locally until saved). localKey keeps v-for keys
// and editing state stable for drafts.
interface DeviceCard {
  localKey: number;
  id?: string;
  name: string;
  ip: string;
  aclIds: string[];
}

let nextLocalKey = 1;

function blankCard(): DeviceCard {
  return { localKey: nextLocalKey++, name: "", ip: "", aclIds: [] };
}

const devices = ref<DeviceCard[]>([blankCard()]);
// localKey of the saved card being edited (drafts are always in edit mode).
const editingKey = ref<number | null>(null);
// Pre-edit copy of the saved card being edited, so Cancel can restore it.
let editSnapshot: DeviceCard | null = null;

// Per-device mutation flag (save/delete/pause a single card); actions are
// immediate, so overlapping single-device requests would race each other.
const busy = ref(false);
// Bulk pause/enable-all runs on its own flag so it never spins or disables a
// card's Save button, creating/editing a device is unrelated to enabling or
// disabling every device at once.
const bulkBusy = ref(false);
// Errors live in the store so they survive the menu closing (which remounts
// this panel). They only clear when the next mutation starts.
const applyError = computed({
  get: () => deviceStore.lastError,
  set: (v: string | null) => (deviceStore.lastError = v),
});
// localKey of the device whose delete awaits an inline "are you sure?".
const confirmingDeleteKey = ref<number | null>(null);

// Credential popup: which enable action is awaiting a ticket/TOTP, plus the
// dialog's in-flight/error/rate-limit state. A single device, or "all" for
// enable-all.
const showCredentialsDialog = ref(false);
const pendingEnable = ref<DeviceCard | "all" | null>(null);
const credentialsDialogError = ref<string | null>(null);
const credentialsDialogLoading = ref(false);
const credentialsDialogRateLimitSeconds = ref(0);

// Capability only: offer any persistent-capable knoc, grant or not.
const eligibleAcls = computed(() => props.acls.filter((acl) => acl.PersistentAndAgenticEnabled));

// ---- IP → flag emoji lookup (debounced) ----
const ipFlagCache = new Map<string, string>();
const ipFlags = ref<Record<string, string>>({});
const ipInfoAvailable = ref<boolean | null>(null);
let flagLookupTimer: ReturnType<typeof setTimeout> | null = null;

function lookupFlags() {
  const ips = devices.value.map((e) => e.ip.trim()).filter((ip) => ip && isValidIP(ip) && !ipFlagCache.has(ip));
  if (ips.length === 0 || ipInfoAvailable.value === false) return;

  api
    .post<Record<string, { country_code?: string; is_private?: boolean }>>("/ipinfo", { ips })
    .then((res) => {
      ipInfoAvailable.value = true;
      for (const [ip, info] of Object.entries(res.data)) {
        const flag = info.is_private
          ? PLACEHOLDER_FLAG
          : info.country_code
            ? countryCodeToFlag(info.country_code)
            : NOT_FOUND_FLAG;
        ipFlagCache.set(ip, flag);
      }
      for (const ip of ips) {
        if (!ipFlagCache.has(ip)) ipFlagCache.set(ip, PLACEHOLDER_FLAG);
      }
      ipFlags.value = Object.fromEntries(ipFlagCache);
    })
    .catch((err: unknown) => {
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        ipInfoAvailable.value = false;
      }
    });
}

watch(
  () => devices.value.map((e) => e.ip.trim()),
  () => {
    if (flagLookupTimer) clearTimeout(flagLookupTimer);
    flagLookupTimer = setTimeout(lookupFlags, IP_LOOKUP_DEBOUNCE_MS);
  },
  { deep: true },
);

// ---- pending-add tracking (persisted, see original rationale) ----
const PENDING_STORAGE_KEY = "persistentIPs.pendingAdd";

function loadPendingFromStorage(): Set<string> {
  try {
    const raw = sessionStorage.getItem(PENDING_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function savePendingToStorage(set: Set<string>) {
  try {
    sessionStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // storage quota or disabled, it's only a UX hint
  }
}

const pendingAddIPs = ref<Set<string>>(loadPendingFromStorage());

function markPending(ips: string[]) {
  for (const ip of ips) pendingAddIPs.value.add(ip);
  savePendingToStorage(pendingAddIPs.value);
}

// Same collapse the agentic popup and knoc-card chips use, so every surface
// agrees on what a status code means. Revoking is deliberately NOT issued,
// access on its way out must not paint a green dot.
function isGrantIssued(status: number): boolean {
  const kind = agenticGrantStatusKind(status);
  return kind === "active" || kind === "published";
}

function isRevokingStatus(status: number): boolean {
  return agenticGrantStatusKind(status) === "revoking";
}

// A companion grant for this IP is still being torn down. Re-granting mid-
// revoke would race the in-flight revoke, so saves/resumes wait it out (the
// status clears within seconds once the agent acknowledges).
function ipRevoking(ip: string): boolean {
  if (!ip) return false;
  return props.acls.some((acl) =>
    (acl.PersistentIPs ?? []).some((c) => c.IP === ip && c.Source !== "agentic_access" && isRevokingStatus(c.Status)),
  );
}

function deviceRevoking(device: DeviceCard): boolean {
  return ipRevoking(device.ip.trim());
}

// Clear the "pending" marker once the grant is issued (incl. Published for passive backends), so the dot turns green.
watch(
  () => props.acls,
  (acls) => {
    if (pendingAddIPs.value.size === 0) return;
    let changed = false;
    for (const acl of acls) {
      for (const cip of acl.PersistentIPs ?? []) {
        if (isGrantIssued(cip.Status) && pendingAddIPs.value.has(cip.IP)) {
          pendingAddIPs.value.delete(cip.IP);
          changed = true;
        }
      }
    }
    if (changed) savePendingToStorage(pendingAddIPs.value);
  },
  { immediate: true, deep: true },
);

// IP → dot state. Agentic-sourced IPs excluded. Revoking outranks granted for
// a shared IP (the teardown must be visible), granted outranks pending.
type DelegatedDotState = "granted" | "pending" | "revoking";
const DOT_STATE_RANK: Record<DelegatedDotState, number> = { pending: 0, granted: 1, revoking: 2 };

const delegatedIPState = computed(() => {
  const map = new Map<string, DelegatedDotState>();
  for (const acl of props.acls) {
    for (const cip of acl.PersistentIPs ?? []) {
      if (cip.Source === "agentic_access") continue;
      let state: DelegatedDotState = "pending";
      if (isRevokingStatus(cip.Status)) state = "revoking";
      else if (isGrantIssued(cip.Status) && !pendingAddIPs.value.has(cip.IP)) state = "granted";
      const current = map.get(cip.IP);
      if (current === undefined || DOT_STATE_RANK[state] > DOT_STATE_RANK[current]) map.set(cip.IP, state);
    }
  }
  return map;
});

const delegatedIPStatus = computed(() => {
  let hasPending = false;
  let hasGranted = false;
  for (const state of delegatedIPState.value.values()) {
    if (state === "granted") hasGranted = true;
    else hasPending = true;
  }
  return { hasPending, hasGranted };
});

// One at-a-glance status per device: a colour-coded dot (with a screen-reader
// label) derived purely from the live grant. There is no stored on/off flag.
// A device is on iff it currently holds a grant.
type CompanionState = "revoking" | "granted" | "pending" | "off";

const STATE_META: Record<CompanionState, { label: string; dot: string }> = {
  revoking: { label: "persistentIPPanel.statusRevoking", dot: "bg-warning animate-pulse" },
  granted: { label: "persistentIPPanel.statusGranted", dot: "bg-green-500" },
  pending: { label: "persistentIPPanel.statusPending", dot: "bg-orange-500" },
  off: { label: "persistentIPPanel.statusOff", dot: "bg-muted-foreground/40" },
};

// Live grant-derived state for one device.
function grantState(device: DeviceCard): CompanionState {
  const s = delegatedIPState.value.get(device.ip.trim());
  if (s === "revoking") return "revoking";
  if (s === "granted") return "granted";
  if (s !== undefined) return "pending";
  return "off";
}

// The grant state can flip rapidly while access reconciles (pending ⇄ granted ⇄
// revoking) as session-status polls land, making the status label flicker.
// Debounce the *displayed* grant state (keyed by stable server id) so a new
// value is only shown once it has held for STATE_DEBOUNCE_MS. Drafts (edit mode,
// no label) and any id not yet committed fall back to live, so nothing is
// delayed needlessly on first render.
const STATE_DEBOUNCE_MS = 600;

const liveGrantStates = computed<Record<string, CompanionState>>(() => {
  const m: Record<string, CompanionState> = {};
  for (const d of devices.value) {
    if (d.id) m[d.id] = grantState(d);
  }
  return m;
});

const debouncedGrantStates = refDebounced(liveGrantStates, STATE_DEBOUNCE_MS);

function companionState(device: DeviceCard): CompanionState {
  if (!device.id) return grantState(device);
  return debouncedGrantStates.value[device.id] ?? grantState(device);
}

// Whether a device is currently on. Access is per-session: a device holds no
// grant until enabled and grants die with the session, so this reads the live
// grant (not the debounced dot). A deliberate enable/disable click flips the
// toggle immediately. "pending" counts as on so the toggle stays consistent
// while a just-enabled grant lands.
function deviceOn(device: DeviceCard): boolean {
  const s = grantState(device);
  return s === "granted" || s === "pending";
}

// Per-knoc live grant status for one companion device, from the session-status
// payload. The same shape the agentic panel feeds ReachExpander, so both
// panels show identical per-knoc chips.
function aclStatusesFor(device: DeviceCard): { aclID: string; status: number }[] {
  const ip = device.ip.trim();
  if (!ip) return [];
  const result: { aclID: string; status: number }[] = [];
  for (const acl of props.acls) {
    if (!acl.ACLID || !device.aclIds.includes(acl.ACLID)) continue;
    const cip = (acl.PersistentIPs ?? []).find((c) => c.IP === ip && c.Source !== "agentic_access");
    if (cip) result.push({ aclID: acl.ACLID, status: cip.Status });
  }
  return result;
}

const savedDeviceCount = computed(() => devices.value.filter((d) => d.id || d.ip.trim()).length);

watch(
  [savedDeviceCount, () => delegatedIPStatus.value.hasPending],
  () => emit("summary", { count: savedDeviceCount.value, hasPending: delegatedIPStatus.value.hasPending }),
  { immediate: true },
);

// Rebuild the saved cards from the store (after load and every server
// mutation) so they always mirror what the server holds, then re-append any
// local drafts. A half-typed card must survive other cards' saves.
function syncFromStore() {
  const drafts = devices.value.filter((d) => !d.id);
  const saved: DeviceCard[] = deviceStore.devices.map((d) => ({
    localKey: nextLocalKey++,
    id: d.id,
    name: d.name,
    ip: d.ip,
    aclIds: (d.aclIds ?? []).filter((id) => eligibleAcls.value.some((a) => a.ACLID === id)),
  }));
  devices.value = [...saved, ...drafts];
  if (devices.value.length === 0) {
    devices.value = [blankCard()];
  }
  editingKey.value = null;
}

onMounted(async () => {
  await deviceStore.fetchDevices();
  if (deviceStore.devices.length > 0) {
    // Devices already exist: show them collapsed, with no add form open.
    // Dropping the pristine initial draft first stops syncFromStore from
    // re-appending it as an open "new device" card.
    devices.value = [];
    syncFromStore();
  }
});

onUnmounted(() => {
  if (flagLookupTimer) clearTimeout(flagLookupTimer);
});

// ---- device editing ----
// Drafts (no id yet) are always in edit mode; a saved card edits via editingKey.
function isEditing(device: DeviceCard): boolean {
  return !device.id || editingKey.value === device.localKey;
}

function startEdit(device: DeviceCard) {
  editSnapshot = { ...device, aclIds: [...device.aclIds] };
  editingKey.value = device.localKey;
}

function addDevice() {
  devices.value.push(blankCard());
}

function cancelEdit(device: DeviceCard) {
  if (device.id && editSnapshot) {
    const i = devices.value.findIndex((d) => d.localKey === device.localKey);
    if (i !== -1) devices.value[i] = editSnapshot;
    editingKey.value = null;
  } else {
    removeLocal(device);
  }
  editSnapshot = null;
}

function removeLocal(device: DeviceCard) {
  devices.value = devices.value.filter((d) => d.localKey !== device.localKey);
  if (devices.value.length === 0) {
    devices.value.push(blankCard());
  }
  if (editingKey.value === device.localKey) {
    editingKey.value = null;
  }
}

function isValidIP(ip: string): boolean {
  const trimmed = ip.trim();
  if (!trimmed) return false;
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(trimmed)) {
    return trimmed.split(".").every((part) => {
      const num = Number(part);
      return num >= 0 && num <= 255 && !(part.length > 1 && part.startsWith("0"));
    });
  }
  try {
    const url = new URL(`http://[${trimmed}]`);
    const parsed = url.hostname.slice(1, -1);
    const recheck = new URL(`http://[${parsed}]`);
    return recheck.hostname === url.hostname;
  } catch {
    return false;
  }
}

function deviceValid(device: DeviceCard): boolean {
  return isValidIP(device.ip) && device.name.trim().length > 0 && device.aclIds.length > 0;
}

// A device already saved server-side (it carries the server's id).
function isSavedDevice(device: DeviceCard): boolean {
  return Boolean(device.id);
}

// Bulk pause/enable-all only earns its footer row once there are several devices;
// for a single device the per-row pause/resume already does the job.
const hasMultipleSavedDevices = computed(() => deviceStore.devices.length > 1);

// Whether enabling this device would grant a knoc that demands a ticket/TOTP.
// Knocs already granted for its IP don't count, re-asserting an existing grant
// is credential-free server-side. Independent of any entered code: credentials
// are collected at enable time via the popup, not while editing.
function deviceRequiresCredentials(device: DeviceCard): boolean {
  const grantedAclIds = new Set(
    aclStatusesFor(device)
      .filter((s) => isGrantIssued(s.status))
      .map((s) => s.aclID),
  );
  return eligibleAcls.value.some(
    (a) => device.aclIds.includes(a.ACLID) && !grantedAclIds.has(a.ACLID) && (a.RequireTicketNumber || a.RequireTOTP),
  );
}

// The merged ticket/TOTP requirement of the enable target's knocs, for the
// credential popup: a requirement holds if any knoc being enabled requires it.
const aclRequirements = computed<ACLRequirements>(() => {
  const target = pendingEnable.value;
  const targets =
    target === "all" ? devices.value.filter((d) => isSavedDevice(d) && !deviceOn(d)) : target ? [target] : [];
  const aclIds = new Set<string>();
  for (const d of targets) for (const id of d.aclIds) aclIds.add(id);
  const relevant = eligibleAcls.value.filter((a) => aclIds.has(a.ACLID));
  return {
    requireTicketNumber: relevant.some((a) => a.RequireTicketNumber),
    requireTOTP: relevant.some((a) => a.RequireTOTP),
    description: targets.length === 1 ? targets[0].name || targets[0].ip : t("persistentIPPanel.allDevices"),
  };
});

function translateError(errKey: string | undefined): string {
  if (errKey && errKey.startsWith("err.")) return t(errKey);
  return errKey || t("common.error");
}

// Only failures surface; success is visible from the cards themselves.
function handleBatchResults(results: { aclID: string; success: boolean; error?: string }[]) {
  const failures = results.filter((r) => !r.success);
  if (failures.length > 0 && failures.length < results.length) {
    const errorMsgs = failures.map((f) => translateError(f.error)).join(", ");
    applyError.value = t("persistentIPPanel.applyPartialFailure", { errors: errorMsgs });
  } else if (failures.length > 0) {
    applyError.value = translateError(failures[0].error);
  }
  authStore.fetchStatus({ force: true });
}

function showApplyException(error: any) {
  if (error?.response?.status === 429) {
    const retryAfter = parseInt(error.response.headers["retry-after"] || String(DEFAULT_RETRY_AFTER_SECONDS), 10);
    applyError.value = t("persistentIPPanel.rateLimited", { seconds: retryAfter });
  } else {
    const errKey = error?.response?.data?.Err;
    applyError.value = errKey && errKey.startsWith("err.") ? t(errKey) : error?.message || t("err.internalServerError");
  }
}

function clearError() {
  applyError.value = null;
}

// Save exactly ONE device: create it (drafts) or update it by id. Saving is
// config-only and never collects credentials. A new device is created off and
// enabled later via its toggle. Editing an enabled device re-asserts its grant
// server-side; if the new config would need credentials the server leaves it
// off for the user to re-enable via the popup. No other card, saved or
// half-typed, is ever sent.
async function saveDevice(device: DeviceCard) {
  if (busy.value || !deviceValid(device) || deviceRevoking(device)) return;

  busy.value = true;
  clearError();
  try {
    const payload = {
      name: device.name.trim(),
      ip: device.ip.trim(),
      aclIds: device.aclIds,
    };

    const results = device.id
      ? await deviceStore.updateDevice(device.id, payload)
      : await deviceStore.createDevice(payload);
    handleBatchResults(results);
    if (!device.id) {
      // A freshly-created draft is now a saved row in the store. Drop the local
      // draft (it never gained the returned id) so syncFromStore doesn't
      // re-append it as a lingering, still-open duplicate card.
      devices.value = devices.value.filter((d) => d.localKey !== device.localKey);
    }
    editSnapshot = null;
    syncFromStore();
  } catch (error: any) {
    showApplyException(error);
  } finally {
    busy.value = false;
  }
}

function requestDelete(device: DeviceCard) {
  if (busy.value) return;
  // Drafts are local-only; no confirmation needed to discard them.
  if (!isSavedDevice(device)) {
    removeLocal(device);
    return;
  }
  confirmingDeleteKey.value = device.localKey;
}

async function confirmDelete(device: DeviceCard) {
  confirmingDeleteKey.value = null;
  if (busy.value || !device.id) return;

  busy.value = true;
  clearError();
  try {
    await deviceStore.deleteDevice(device.id);
    authStore.fetchStatus({ force: true });
    syncFromStore();
  } catch (error: any) {
    showApplyException(error);
  } finally {
    busy.value = false;
  }
}

// Low-level mutations: they throw so the caller decides where the error goes
// (an inline banner for a direct toggle, the popup for a credential enable).
async function applyDeviceEnabled(device: DeviceCard, enabled: boolean, creds: GrantCredentials) {
  if (!device.id) return;
  // Enabling grants (mark pending so the dot stays orange until confirmed) and
  // may be credential-gated; disabling only revokes and never is.
  if (enabled) markPending([device.ip.trim()]);
  const results = await deviceStore.setDeviceEnabled(device.id, { enabled, ...(enabled ? creds : {}) });
  handleBatchResults(results);
  syncFromStore();
}

async function applyAllEnabled(enabled: boolean, creds: GrantCredentials) {
  if (enabled) markPending(devices.value.filter(isSavedDevice).map((d) => d.ip.trim()));
  const results = await deviceStore.setAllEnabled({ enabled, ...(enabled ? creds : {}) });
  handleBatchResults(results);
  syncFromStore();
}

// Toggle one device. Enabling a credential-gated device opens the popup;
// everything else (disabling, or enabling a credential-free device) applies now.
function toggleEnabled(device: DeviceCard) {
  if (busy.value || !device.id) return;
  if (deviceOn(device)) {
    void runDeviceEnabled(device, false, {});
    return;
  }
  if (deviceRevoking(device)) return;
  if (deviceRequiresCredentials(device)) {
    openEnableDialog(device);
    return;
  }
  void runDeviceEnabled(device, true, {});
}

async function runDeviceEnabled(device: DeviceCard, enabled: boolean, creds: GrantCredentials) {
  busy.value = true;
  clearError();
  try {
    await applyDeviceEnabled(device, enabled, creds);
  } catch (error: any) {
    showApplyException(error);
  } finally {
    busy.value = false;
  }
}

async function runAllEnabled(enabled: boolean) {
  if (bulkBusy.value) return;
  bulkBusy.value = true;
  clearError();
  try {
    await applyAllEnabled(enabled, {});
  } catch (error: any) {
    showApplyException(error);
  } finally {
    bulkBusy.value = false;
  }
}

// Disable every device now (no credentials); enabling all routes through the
// popup when any device being enabled needs credentials.
function disableAll() {
  void runAllEnabled(false);
}

function enableAll() {
  if (bulkBusy.value) return;
  const anyNeedsCreds = devices.value.some((d) => isSavedDevice(d) && !deviceOn(d) && deviceRequiresCredentials(d));
  if (anyNeedsCreds) {
    openEnableDialog("all");
    return;
  }
  void runAllEnabled(true);
}

function openEnableDialog(target: DeviceCard | "all") {
  pendingEnable.value = target;
  credentialsDialogError.value = null;
  credentialsDialogRateLimitSeconds.value = 0;
  credentialsDialogLoading.value = false;
  showCredentialsDialog.value = true;
}

function handleCredentialsClose() {
  showCredentialsDialog.value = false;
  pendingEnable.value = null;
  credentialsDialogError.value = null;
  credentialsDialogLoading.value = false;
}

// Enable the pending target with the popup's credentials. On failure the popup
// stays open showing the error (429 → the dialog's rate-limit countdown); the
// backend rolls the enable back, so the device stays disabled.
async function handleCredentialsSubmit(creds: GrantCredentials) {
  const target = pendingEnable.value;
  if (!target) return;
  const bulk = target === "all";
  credentialsDialogError.value = null;
  credentialsDialogRateLimitSeconds.value = 0;
  credentialsDialogLoading.value = true;
  clearError();
  if (bulk) bulkBusy.value = true;
  else busy.value = true;
  try {
    if (bulk) await applyAllEnabled(true, creds);
    else await applyDeviceEnabled(target, true, creds);
    handleCredentialsClose();
  } catch (error: any) {
    if (error?.response?.status === 429) {
      credentialsDialogRateLimitSeconds.value = parseInt(
        error.response.headers["retry-after"] || String(DEFAULT_RETRY_AFTER_SECONDS),
        10,
      );
      credentialsDialogError.value = null;
    } else {
      const errKey = error?.response?.data?.Err;
      credentialsDialogError.value =
        errKey && errKey.startsWith("err.") ? t(errKey) : error?.message || t("common.error");
    }
  } finally {
    credentialsDialogLoading.value = false;
    if (bulk) bulkBusy.value = false;
    else busy.value = false;
  }
}
</script>

<template>
  <div class="space-y-4">
    <p class="text-xs text-muted-foreground">{{ t("persistentIPPanel.description") }}</p>

    <div class="space-y-2">
      <div
        v-for="device in devices"
        :key="device.id ?? `local-${device.localKey}`"
        class="rounded-md border border-input bg-background px-3 py-2"
      >
        <!-- Compact (saved) row -->
        <div v-if="!isEditing(device)" class="flex items-center gap-2.5">
          <!-- Colour carries the state visually; the label is screen-reader-only -->
          <span
            class="h-2 w-2 shrink-0 rounded-full"
            :class="STATE_META[companionState(device)].dot"
            :aria-label="t(STATE_META[companionState(device)].label)"
            role="status"
          ></span>
          <span v-if="ipFlags[device.ip.trim()]" class="shrink-0 text-base leading-none" aria-hidden="true">{{
            ipFlags[device.ip.trim()]
          }}</span>
          <div class="min-w-0 flex-1" :class="{ 'opacity-60': !deviceOn(device) }">
            <p class="truncate text-sm font-medium">{{ device.name || device.ip }}</p>
            <p v-if="device.name" class="truncate font-mono text-xs text-muted-foreground">{{ device.ip }}</p>
          </div>
          <!-- All row actions in one right-aligned, evenly-sized cluster -->
          <div class="flex shrink-0 items-center">
            <button
              v-if="isSavedDevice(device)"
              type="button"
              :aria-label="
                deviceOn(device)
                  ? t('persistentIPPanel.disableDevice', { name: device.name || device.ip })
                  : t('persistentIPPanel.enableDevice', { name: device.name || device.ip })
              "
              :disabled="busy || (!deviceOn(device) && deviceRevoking(device))"
              class="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
              @click="toggleEnabled(device)"
            >
              <font-awesome-icon
                :icon="['fass', deviceOn(device) ? 'circle-pause' : 'circle-play']"
                class="h-4 w-4"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              :aria-label="`${t('common.edit')} ${device.name || device.ip}`"
              :disabled="busy"
              class="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
              @click="startEdit(device)"
            >
              <PencilSquareIcon class="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              :aria-label="`${t('common.delete')} ${device.name || device.ip}`"
              :disabled="busy || confirmingDeleteKey === device.localKey"
              class="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-danger disabled:opacity-50"
              @click="requestDelete(device)"
            >
              <TrashIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <ReachExpander
          v-if="!isEditing(device)"
          :acl-ids="device.aclIds"
          :acls="props.acls"
          :acl-statuses="aclStatusesFor(device)"
          class="mt-1"
        />

        <!-- Inline delete confirmation (deletion revokes access immediately) -->
        <div
          v-if="!isEditing(device) && confirmingDeleteKey === device.localKey"
          class="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-input pt-2"
        >
          <p class="text-xs text-muted-foreground">
            {{ t("persistentIPPanel.confirmDelete", { name: device.name || device.ip }) }}
          </p>
          <div class="flex shrink-0 items-center gap-2">
            <button
              type="button"
              class="inline-flex h-7 items-center rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              @click="confirmingDeleteKey = null"
            >
              {{ t("common.cancel") }}
            </button>
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1 rounded-md border border-destructive px-2 text-xs text-danger hover:bg-destructive/10 disabled:opacity-50"
              :disabled="busy"
              @click="confirmDelete(device)"
            >
              <TrashIcon class="h-3.5 w-3.5" aria-hidden="true" />
              {{ t("common.delete") }}
            </button>
          </div>
        </div>

        <!-- Editing row -->
        <div v-if="isEditing(device)" class="space-y-3">
          <div class="flex items-start gap-2">
            <input
              v-model="device.name"
              type="text"
              :aria-label="t('persistentIPPanel.ipNamePlaceholder')"
              :placeholder="t('persistentIPPanel.ipNamePlaceholder')"
              :aria-invalid="device.ip.trim() && !device.name.trim() ? 'true' : undefined"
              :class="[
                'h-9 w-24 shrink-0 rounded-md border bg-background px-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-32',
                device.ip.trim() && !device.name.trim() ? 'border-destructive' : 'border-input',
              ]"
            />
            <span
              v-if="ipInfoAvailable !== false"
              class="inline-flex w-5 shrink-0 justify-center pt-2 text-base leading-none"
              aria-hidden="true"
              >{{ ipFlags[device.ip.trim()] || "" }}</span
            >
            <div class="flex min-w-0 flex-1 flex-col">
              <input
                v-model="device.ip"
                type="text"
                :aria-label="t('persistentIPPanel.ipAddressPlaceholder')"
                :placeholder="t('persistentIPPanel.ipAddressPlaceholder')"
                :aria-invalid="device.ip.trim() && !isValidIP(device.ip) ? 'true' : undefined"
                :aria-describedby="
                  device.ip.trim() && !isValidIP(device.ip) ? `ip-error-${device.localKey}` : undefined
                "
                :class="[
                  'h-9 w-full rounded-md border bg-background px-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  device.ip.trim() && !isValidIP(device.ip) ? 'border-destructive' : 'border-input',
                ]"
              />
              <p
                v-if="device.ip.trim() && !isValidIP(device.ip)"
                :id="`ip-error-${device.localKey}`"
                class="mt-0.5 text-xs text-danger"
              >
                {{ t("persistentIPPanel.invalidIP") }}
              </p>
            </div>
          </div>

          <KnocSearch :model-value="device.aclIds" :acls="eligibleAcls" @update:model-value="device.aclIds = $event" />

          <p v-if="deviceRevoking(device)" class="text-xs text-muted-foreground" role="status">
            {{ t("persistentIPPanel.revokeInProgress") }}
          </p>

          <div class="flex items-center gap-2">
            <button
              type="button"
              :disabled="busy || !deviceValid(device) || deviceRevoking(device)"
              class="themed-button h-8 px-3 text-sm"
              :class="(Boolean(theme) && `theme-${theme}${highContrast ? '-hc' : ''}`) || 'theme-default'"
              @click="saveDevice(device)"
            >
              <span
                v-if="busy"
                aria-hidden="true"
                class="mr-1.5 inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
              ></span>
              {{ busy ? t("persistentIPPanel.applying") : t("common.save") }}
            </button>
            <button
              type="button"
              :disabled="busy"
              class="inline-flex h-8 items-center rounded-md px-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
              @click="cancelEdit(device)"
            >
              {{ t("common.cancel") }}
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        @click="addDevice"
      >
        <PlusIcon class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t("persistentIPPanel.addDevice") }}
      </button>
    </div>

    <div v-if="applyError" role="alert" class="rounded-md border border-destructive bg-destructive/10 p-2">
      <p class="text-xs text-danger">{{ applyError }}</p>
    </div>

    <div
      v-if="hasMultipleSavedDevices"
      class="flex flex-wrap items-center justify-end gap-2 border-t border-input pt-4"
    >
      <button
        type="button"
        :disabled="bulkBusy"
        class="inline-flex h-9 items-center gap-1.5 rounded-md bg-secondary px-3 text-sm text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
        @click="disableAll()"
      >
        <font-awesome-icon :icon="['fass', 'circle-pause']" class="h-4 w-4" aria-hidden="true" />
        {{ t("persistentIPPanel.disableAll") }}
      </button>
      <button
        type="button"
        :disabled="bulkBusy"
        class="inline-flex h-9 items-center gap-1.5 rounded-md bg-secondary px-3 text-sm text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
        @click="enableAll()"
      >
        <font-awesome-icon :icon="['fass', 'circle-play']" class="h-4 w-4" aria-hidden="true" />
        {{ t("persistentIPPanel.enableAll") }}
      </button>
    </div>

    <GrantCredentialsDialog
      :show="showCredentialsDialog"
      :acl="aclRequirements"
      :error="credentialsDialogError"
      :is-loading="credentialsDialogLoading"
      :rate-limit-seconds="credentialsDialogRateLimitSeconds"
      @close="handleCredentialsClose"
      @submit="handleCredentialsSubmit"
      @rate-limit-expired="credentialsDialogRateLimitSeconds = 0"
    />
  </div>
</template>
